<template>
    <vx-expandable-card toggleable id="type" :active="active">
        <template v-slot:header>
            <div class="flex items-center pb-1 lg:pb-0" data-test="quick-blast-type-header">
                <span
                    class="pr-1.5 mr-2 border-y-0 border-l-0 border-r border-solid sm:pr-3 sm:mr-4"
                    :class="{
                        'border-r-transparent': !editable && !setByCorp,
                        'border-r-slate-400': editable || setByCorp,
                    }"
                >
                    Type
                </span>
                <span v-if="editable || setByCorp" class="flex gap-3">
                    <span class="capitalize text-xs font-normal text-slate-700 sm:text-base sm:font-semibold">
                        {{ props.modelValue }}
                    </span>
                    <vx-popover v-if="setByCorp" hover arrow placement="top">
                        <vx-badge size="2xs" color="slate" class="cursor-default">
                            Set by Corp
                            <font-awesome-icon :icon="faCrown"></font-awesome-icon>
                        </vx-badge>
                        <template v-slot:content>
                            <div class="text-center">
                                {{ corporateMessage }}
                            </div>
                        </template>
                    </vx-popover>
                </span>
            </div>
        </template>
        <template v-if="editable && !setByCorp && !props.success" v-slot:headerRight>
            <vx-button @click.stop="emit('update:active', true)" color="muted" size="md">
                <span class="inline-block">Edit</span>
                <font-awesome-icon
                    widthAuto
                    class="text-base hidden! sm:inline-block!"
                    :icon="faPencil"
                ></font-awesome-icon>
            </vx-button>
        </template>
        <template v-slot:content>
            <div class="flex flex-col items-start">
                <strong class="text-slate-900 text-sm font-semibold sm:text-base sm:leading-6">
                    What audience type are you messaging?
                </strong>
                <span class="text-slate-500 text-xs font-normal mb-2">
                    This selection will limit the Segments you can send to.
                </span>
            </div>

            <vx-radio-selection :modelValue="props.modelValue" @update:modelValue="updateType($event)">
                <vx-radio-selection-item :value="AUDIENCE_TYPE_MARKETING" data-test="quick-blast-type-marketing">
                    <template v-slot:header> Marketing </template>
                    <template v-slot:content>
                        <ul
                            class="list-disc list-outside ml-10 lg:ml-4"
                            v-html="audienceTypeDescription(AUDIENCE_TYPE_MARKETING)"
                        ></ul>
                    </template>
                </vx-radio-selection-item>
                <vx-radio-selection-item
                    :value="AUDIENCE_TYPE_TRANSACTIONAL"
                    data-test="quick-blast-type-transactional"
                >
                    <template v-slot:header> Transactional </template>
                    <template v-slot:content>
                        <ul
                            class="list-disc list-outside ml-10 lg:ml-4"
                            v-html="audienceTypeDescription(AUDIENCE_TYPE_TRANSACTIONAL)"
                        ></ul>
                    </template>
                </vx-radio-selection-item>
            </vx-radio-selection>
            <vx-button
                @click="emit('continue')"
                class="mt-4 lg:mt-6"
                size="lg"
                :block="true"
                :disabled="props.disabled"
                data-test="type-continue"
            >
                Continue
                <font-awesome-icon widthAuto class="text-base" :icon="faArrowTurnDown"></font-awesome-icon>
            </vx-button>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import {
    VxExpandableCard,
    VxRadioSelection,
    VxRadioSelectionItem,
    VxButton,
    VxBadge,
    VxPopover,
} from '@voxie/frontend-components';
import { computed, watch, ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPencil, faArrowTurnDown, faCrown } from '@fortawesome/pro-solid-svg-icons';
import { audienceTypeDescription, AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL } from '~/constants/campaign';
import { useMessageRules } from './useMessageRules';
import { capitalize } from '~/utils/helpers';

const props = defineProps({
    modelValue: {
        type: String,
    },
    active: {
        type: Boolean,
        default: false,
    },
    success: {
        type: Boolean,
        default: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'update:active', 'continue']);

const setByCorp = ref(false);

const updateType = (type) => {
    emit('update:modelValue', type);
};

const editable = computed(() => !props.active && props.modelValue);

const { marketingAllowed, transactionalAllowed } = useMessageRules();

const corporateMessage = computed(() => {
    if (!setByCorp.value || !props.modelValue) {
        return '';
    }

    const type = capitalize(props.modelValue);
    return `Corporate has limited quick blast to ${type} campaigns only.`;
});

watch(
    [marketingAllowed, transactionalAllowed],
    ([marketing, transactional]) => {
        const onlyOneEnabled = marketing !== transactional;

        if (onlyOneEnabled) {
            setByCorp.value = true;
            emit('update:modelValue', marketing ? AUDIENCE_TYPE_MARKETING : AUDIENCE_TYPE_TRANSACTIONAL);
            emit('continue');
        }
    },
    { immediate: true }
);
</script>
